import React, { useState, useEffect, useMemo } from "react";
import { getLegislationsRapide } from "../../services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import type { LegislationRapide } from "../../types/types";
import { getFileName, resolveMediaUrl } from "../../utils/media";

const ITEMS_PER_PAGE = 10;

const Legislation: React.FC = () => {
  const [legislations, setLegislations] = useState<LegislationRapide[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    fetchLegislations();
  }, []);

  const fetchLegislations = async () => {
    try {
      setLoading(true);
      const response = await getLegislationsRapide();
      setLegislations(response.data);
    } catch (error) {
      console.error("Error fetching legislations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileNameValue: string, fallbackName: string) => {
    const downloadKey = `${fileNameValue}-download`;
    if (downloadingFiles.has(downloadKey)) return;

    try {
      setDownloadingFiles((prev) => new Set(prev).add(downloadKey));

      if (fileNameValue) {
        const link = document.createElement("a");
        link.href = resolveMediaUrl(fileNameValue);
        link.download = getFileName(fileNameValue) || fallbackName;
        link.click();
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("حدث خطأ أثناء تحميل الملف");
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  const handleView = async (fileNameValue: string) => {
    const viewKey = `${fileNameValue}-view`;
    if (downloadingFiles.has(viewKey)) return;

    try {
      setDownloadingFiles((prev) => new Set(prev).add(viewKey));

      if (fileNameValue) {
        window.open(resolveMediaUrl(fileNameValue), "_blank");
      }
    } catch (error) {
      console.error("Error viewing file:", error);
      alert("حدث خطأ أثناء معاينة الملف");
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(viewKey);
        return newSet;
      });
    }
  };

  // البحث في التشريعات مع التصفية
  const filteredLegislations = useMemo(() => {
    return legislations.filter(
      (law) =>
        (law.titrearabe?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (law.titrefrancais?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (law.datesortie?.toLowerCase() || "").includes(searchTerm.toLowerCase())
    );
  }, [legislations, searchTerm]);

  // التقسيم إلى صفحات
  const paginatedLegislations = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredLegislations.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredLegislations, currentPage]);

  const totalPages = Math.ceil(filteredLegislations.length / ITEMS_PER_PAGE);

  const isDownloading = (
    lawId: number,
    fileType: "arabe" | "francais",
    action: "download" | "view"
  ) => downloadingFiles.has(`${lawId}-${fileType}-${action}`);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white py-20 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">التشريعات والقوانين</h1>
          <p className="text-xl">
            النصوص القانونية والتشريعات المنظمة للقطاع العقاري
          </p>
        </div>
      </section>

      {/* Legislation Section */}
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Search Box */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              className="w-full pr-10 pl-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent"
              placeholder="ابحث في التشريعات..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-mauritania-green-dark text-4xl animate-spin mb-4"
            />
            <p className="text-gray-600">جاري تحميل التشريعات...</p>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {paginatedLegislations.map((law) => (
                <div
                  key={law.id}
                  className="bg-white p-6 rounded-lg shadow-custom hover:shadow-md transition duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-mauritania-green-dark mb-2">
                        {law.titrearabe || "بدون عنوان عربي"}
                      </h3>
                      {law.titrefrancais && (
                        <p className="text-gray-600 mb-2">
                          {law.titrefrancais}
                        </p>
                      )}
                      <div className="text-sm text-gray-500">
                        {law.datesortie
                          ? new Date(law.datesortie).toLocaleDateString("ar-MA")
                          : "غير محدد التاريخ"}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Arabic File */}
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => handleView(law.fichierarabe)}
                          disabled={isDownloading(law.id, "arabe", "view") || !law.fichierarabe}
                          className="bg-mauritania-green text-white py-2 px-4 rounded-md hover:bg-mauritania-green-dark transition duration-300 mb-2 sm:mb-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="معاينة الملف العربي"
                        >
                          {isDownloading(law.id, "arabe", "view") ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white" />
                          ) : (
                            "معاينة عربي"
                          )}
                        </button>
                        <button
                          onClick={() =>
                            handleDownload(law.fichierarabe, `تشريع-عربي-${law.id}.pdf`)
                          }
                          disabled={isDownloading(law.id, "arabe", "download") || !law.fichierarabe}
                          className="text-mauritania-green hover:text-mauritania-green-dark text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          title="تحميل الملف العربي"
                        >
                          {isDownloading(law.id, "arabe", "download")
                            ? "جاري التحميل..."
                            : "تحميل"}
                        </button>
                      </div>

                      {/* French File */}
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => handleView(law.fichierfrancais)}
                          disabled={isDownloading(law.id, "francais", "view") || !law.fichierfrancais}
                          className="bg-mauritania-green text-white py-2 px-4 rounded-md hover:bg-mauritania-green-dark transition duration-300 mb-2 sm:mb-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="معاينة الملف الفرنسي"
                        >
                          {isDownloading(law.id, "francais", "view") ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white" />
                          ) : (
                            "معاينة فرنسي"
                          )}
                        </button>
                        <button
                          onClick={() =>
                            handleDownload(
                              law.fichierfrancais,
                              `تشريع-فرنسي-${law.id}.pdf`
                            )
                          }
                          disabled={
                            isDownloading(law.id, "francais", "download") ||
                            !law.fichierfrancais
                          }
                          className="text-mauritania-green hover:text-mauritania-green-dark text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          title="تحميل الملف الفرنسي"
                        >
                          {isDownloading(law.id, "francais", "download")
                            ? "جاري التحميل..."
                            : "تحميل"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <div className="flex space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-4 py-2 border rounded-md disabled:opacity-50"
                  >
                    السابق
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 border rounded-md ${
                          currentPage === page
                            ? "bg-mauritania-green text-white"
                            : ""
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border rounded-md disabled:opacity-50"
                  >
                    التالي
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Legislation;
